import { getEnv } from "@/lib/get_env";
import { useUserStore } from "@/stores/user-store";

// License types
export interface LicenseCreateRequest {
  organization_id: string;
  organization_name: string;
  assigned_user_email: string; // NEW: Required - encoded in license key
  custom_expiration_days?: number;
  custom_expiration_months?: number; // NEW
  custom_expiration_years?: number; // NEW
  notes?: string;
}

export interface LicenseUpdateRequest {
  is_active?: boolean;
  notes?: string;
}

export interface License {
  id: number;
  license_key: string;
  license_holder: string;
  organization_id: string;
  organization_name: string;
  created_by_user_id: string;
  created_by_user_email: string;
  expiration_date: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  days_until_expiration?: number;
  is_expired: boolean;
  is_custom_organization?: boolean; // Whether org doesn't exist in portal
}

export interface LicenseStatistics {
  total_licenses: number;
  active_licenses: number;
  inactive_licenses: number;
  expired_licenses: number;
  licenses_by_org: Record<string, number>;
}

export interface JFrogTokenCreateRequest {
  organization_id: string;
  expires_in_days?: number;
  include_reference_token?: boolean;
}

export interface JFrogToken {
  id: string;
  token_name: string;
  jfrog_token_id: string;
  organization_id: string;
  organization_name: string;
  created_by_user_id: string;
  created_by_user_email: string;
  expires_at?: string;
  is_active: boolean;
  is_revoked: boolean;
  created_at: string;
  last_used_at?: string;
  use_count: number;
  token_value?: string;
  reference_token?: string;
}

export interface JFrogTokenListResponse {
  tokens: JFrogToken[];
  total: number;
}

export interface ZitadelToken {
  id: string;
  details: {
    sequence: number;
    creation_date: string;
    change_date: string;
    resource_owner: string;
  };
  expiration_date?: string;
  scopes?: string[];
}

export interface ZitadelTokensResponse {
  status: string;
  data: {
    result: ZitadelToken[];
  };
}

export interface ZitadelTokenCreateResponse {
  status: string;
  data: {
    id: string;
    token: string;
    details: {
      sequence: number;
      creation_date: string;
      change_date: string;
      resource_owner: string;
    };
  };
}

// Asset download types
export interface AssetDownloadResponse {
  download_id: number;
  url: string;
}

export interface AssetDownload {
  id: number;
  asset_id?: number;
  asset_path: string;
  asset_name: string;
  asset_version?: string;
  user_id: string;
  user_email: string;
  organization_id: string;
  organization_name: string;
  ip_address?: string;
  user_agent?: string;
  file_size_bytes?: number;
  download_status: string;
  download_duration_ms?: number;
  signed_url_generated_at: string;
  download_initiated_at: string;
  download_completed_at?: string;
  error_message?: string;
}

export type DownloadHistorySortBy = "download_initiated_at" | "organization_name";

export interface DownloadHistoryPage {
  downloads: AssetDownload[];
  total: number;
  page: number;
  page_size: number;
}

export interface DownloadHistoryFilters {
  user_id?: string;
  org_id?: string;
  asset_id?: number;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
  sort_by?: DownloadHistorySortBy;
  sort_dir?: "asc" | "desc";
}

// User invitation types
export interface UserAddPayload {
  profile: {
    first_name: string;
    last_name: string;
    display_name?: string;
  };
  email: {
    email: string;
  };
  user_name: string;
  role_keys?: string[];
}

export interface EmailCheckResponse {
  exists: boolean;
  email: string;
}

class API {
  private getAuthHeader(): string {
    const token = useUserStore.getState().token;
    if (!token) {
      console.error("No token available in store");
    } else {
      console.info("Using token:", token.substring(0, 20) + "...");
    }
    return `Bearer ${token}`;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const response = await fetch(`${getEnv("VITE_BACKEND_SERVICE_URL")}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: this.getAuthHeader(),
          ...options.headers,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          console.error("API error response:", errorData);
          // Handle different error response formats
          if (typeof errorData === "string") {
            errorMessage = errorData;
          } else if (errorData.detail) {
            errorMessage = typeof errorData.detail === "string" ? errorData.detail : JSON.stringify(errorData.detail);
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else {
            errorMessage = JSON.stringify(errorData);
          }
        } catch (e) {
          console.error("Failed to parse error response:", e);
        }
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Existing methods
  async fetchOrgUsers(): Promise<Response> {
    const token = useUserStore.getState().token;
    return fetch(`${getEnv("VITE_BACKEND_SERVICE_URL")}/api/v1/organizations/my-orgs`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // License methods
  async generateLicense(request: LicenseCreateRequest): Promise<License> {
    return this.request<License>("/api/v1/licenses/generate", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async listLicenses(params: {
    org_id?: string;
    include_inactive?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<{ licenses: License[]; total: number; page: number; page_size: number }> {
    const searchParams = new URLSearchParams();
    if (params.org_id) searchParams.append("org_id", params.org_id);
    if (params.include_inactive !== undefined) searchParams.append("include_inactive", String(params.include_inactive));
    if (params.page) searchParams.append("page", String(params.page));
    if (params.page_size) searchParams.append("page_size", String(params.page_size));

    return this.request<{ licenses: License[]; total: number; page: number; page_size: number }>(
      `/api/v1/licenses/list?${searchParams.toString()}`,
    );
  }

  async getLicense(id: number): Promise<License> {
    return this.request<License>(`/api/v1/licenses/${id}`);
  }

  async updateLicense(id: number, request: LicenseUpdateRequest): Promise<License> {
    return this.request<License>(`/api/v1/licenses/${id}/update`, {
      method: "PATCH",
      body: JSON.stringify(request),
    });
  }

  async validateLicense(
    key: string,
    holder: string,
  ): Promise<{ is_valid: boolean; is_active: boolean; license_key: string }> {
    return this.request<{ is_valid: boolean; is_active: boolean; license_key: string }>(`/api/v1/licenses/validate`, {
      method: "POST",
      body: JSON.stringify({ license_key: key, license_holder: holder }),
    });
  }

  async getLicenseStatistics(orgId?: string): Promise<LicenseStatistics> {
    const searchParams = orgId ? `?org_id=${orgId}` : "";
    return this.request<LicenseStatistics>(`/api/v1/licenses/statistics${searchParams}`);
  }

  // JFrog Token methods
  async createJFrogToken(orgId: string, request: JFrogTokenCreateRequest): Promise<JFrogToken> {
    return this.request<JFrogToken>(`/api/v1/organizations/${orgId}/jfrog-tokens`, {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async listJFrogTokens(orgId: string, includeRevoked = false): Promise<JFrogTokenListResponse> {
    const searchParams = new URLSearchParams();
    if (includeRevoked) searchParams.append("include_revoked", "true");
    return this.request<JFrogTokenListResponse>(
      `/api/v1/organizations/${orgId}/jfrog-tokens?${searchParams.toString()}`,
    );
  }

  async revokeJFrogToken(orgId: string, tokenId: string): Promise<{ status: string; message: string }> {
    return this.request<{ status: string; message: string }>(`/api/v1/organizations/${orgId}/jfrog-tokens/${tokenId}`, {
      method: "DELETE",
    });
  }

  async listZitadelTokens(orgId: string): Promise<ZitadelToken[]> {
    const response = await this.request<ZitadelTokensResponse>(`/api/v1/organizations/${orgId}/tokens/list`);

    if (Array.isArray(response.data)) {
      return response.data as ZitadelToken[];
    }

    return response.data?.result || [];
  }

  async generateZitadelToken(orgId: string): Promise<ZitadelTokenCreateResponse["data"]> {
    const response = await this.request<ZitadelTokenCreateResponse>(`/api/v1/organizations/${orgId}/tokens/generate`, {
      method: "POST",
    });
    return response.data;
  }

  async deleteZitadelToken(orgId: string, tokenId: string): Promise<void> {
    await this.request<void>(`/api/v1/organizations/${orgId}/tokens/${tokenId}/delete`, {
      method: "DELETE",
    });
  }

  // Download tracking methods
  async trackAssetDownload(assetId: number): Promise<AssetDownloadResponse> {
    return this.request<AssetDownloadResponse>(`/api/v1/assets/${assetId}/download`);
  }

  async completeDownload(downloadId: number, durationMs?: number): Promise<void> {
    await this.request<{ message: string }>(`/api/v1/assets/downloads/${downloadId}/complete`, {
      method: "POST",
      body: JSON.stringify({
        status: "completed",
        duration_ms: durationMs,
      }),
    });
  }

  async getDownloadHistory(filters: DownloadHistoryFilters): Promise<DownloadHistoryPage> {
    const searchParams = new URLSearchParams();
    if (filters.user_id) searchParams.append("user_id", filters.user_id);
    if (filters.org_id) searchParams.append("org_id", filters.org_id);
    if (filters.asset_id) searchParams.append("asset_id", String(filters.asset_id));
    if (filters.start_date) searchParams.append("start_date", filters.start_date);
    if (filters.end_date) searchParams.append("end_date", filters.end_date);
    if (filters.page) searchParams.append("page", String(filters.page));
    if (filters.page_size) searchParams.append("page_size", String(filters.page_size));
    if (filters.sort_by) searchParams.append("sort_by", filters.sort_by);
    if (filters.sort_dir) searchParams.append("sort_dir", filters.sort_dir);

    return this.request<DownloadHistoryPage>(`/api/v1/assets/downloads/history?${searchParams.toString()}`);
  }

  async exportDownloadHistory(params: { start_date?: string; end_date?: string; export_all?: boolean }): Promise<void> {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append("start_date", params.start_date);
    if (params.end_date) searchParams.append("end_date", params.end_date);
    if (params.export_all) searchParams.append("export_all", "true");

    const response = await fetch(
      `${getEnv("VITE_BACKEND_SERVICE_URL")}/api/v1/assets/downloads/history/export?${searchParams.toString()}`,
      {
        headers: {
          Authorization: this.getAuthHeader(),
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Export failed: HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const parts: string[] = ["download-history"];
    if (params.start_date) parts.push(params.start_date);
    if (params.end_date) parts.push(`to-${params.end_date}`);
    a.download = `${parts.join("-")}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Organization user methods
  async checkEmailInOrg(orgId: string, email: string): Promise<EmailCheckResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append("email", email);
    return this.request<EmailCheckResponse>(
      `/api/v1/organizations/${orgId}/users/check-email?${searchParams.toString()}`,
    );
  }

  async addUser(orgId: string, payload: UserAddPayload): Promise<unknown> {
    return this.request<unknown>(`/api/v1/organizations/${orgId}/users/add`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }
}

export default API;
